import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';
import {
  clearMcpSession,
  getMcpServerUrl,
  persistMcpSession,
  readMcpSessionFromUrl,
  shouldUseGoogleRedirect,
} from 'features/auth/utils/mcpLoginSession';

async function completeMcpLogin(
  mcpSession: string,
  mcpServerUrl: string,
): Promise<string> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new Error('Could not obtain Firebase ID token.');
  }

  const res = await fetch(`${mcpServerUrl}/oauth/firebase-callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mcp_session: mcpSession, id_token: idToken }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      typeof data.message === 'string'
        ? data.message
        : 'MCP login failed.',
    );
  }

  const redirectUrl = data.payload?.redirectUrl;
  if (typeof redirectUrl !== 'string' || !redirectUrl) {
    throw new Error('MCP server did not return a redirect URL.');
  }

  clearMcpSession();
  return redirectUrl;
}

/** Google-only sign-in for the Bagel MCP OAuth bridge. */
export default function useMcpGoogleLogin() {
  const [searchParams] = useSearchParams();
  const mcpServerUrl = useMemo(() => getMcpServerUrl(), []);
  const mcpSession = useMemo(
    () => readMcpSessionFromUrl(searchParams),
    [searchParams],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const completingRef = useRef(false);

  const tryComplete = useCallback(
    async (session: string) => {
      if (!mcpServerUrl || completingRef.current) return;

      const auth = getAuth();
      if (!auth.currentUser) return;

      completingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const redirectUrl = await completeMcpLogin(session, mcpServerUrl);
        window.location.href = redirectUrl;
      } catch (err) {
        completingRef.current = false;
        setError(
          err instanceof Error ? err.message : 'MCP connection failed.',
        );
        setIsLoading(false);
      }
    },
    [mcpServerUrl],
  );

  // Handles return from Google redirect (Android) and users already signed in to Rosie.
  useEffect(() => {
    if (!mcpSession || !mcpServerUrl) return undefined;

    const auth = getAuth();
    if (auth.currentUser) {
      tryComplete(mcpSession).catch(() => {});
      return undefined;
    }

    const unsub = onAuthStateChanged(auth, user => {
      if (user) tryComplete(mcpSession).catch(() => {});
    });
    return unsub;
  }, [mcpSession, mcpServerUrl, tryComplete]);

  const signInWithGoogle = async () => {
    if (!mcpSession || !mcpServerUrl) return;

    setError(null);
    setIsLoading(true);
    persistMcpSession(mcpSession);

    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      if (shouldUseGoogleRedirect()) {
        await signInWithRedirect(auth, provider);
        return;
      }

      await signInWithPopup(auth, provider);
      await tryComplete(mcpSession);
    } catch (e: unknown) {
      setIsLoading(false);
      if (e instanceof FirebaseError) {
        if (
          e.code === 'auth/popup-closed-by-user' ||
          e.code === 'auth/cancelled-popup-request'
        ) {
          return;
        }
        if (e.code === 'auth/account-exists-with-different-credential') {
          setError(
            'This Google account is not linked to Rosie. Sign in at Rosie with Google first to link accounts, then retry MCP.',
          );
          return;
        }
      }
      setError(e instanceof Error ? e.message : 'Google sign-in failed.');
    }
  };

  return {
    mcpSession,
    mcpServerUrl,
    isLoading,
    error,
    signInWithGoogle,
  };
}
